import { atom, selector, selectorFamily } from "recoil";
import ReservationService from "src/services/agenda/reservations";
import { addDays } from "src/shared/global/helpers";
import { dayDefaultAvailableState } from "../components/calendars/state/atoms";
import { sucursalesState } from "../components/autocompletes/state/atoms";
import SucursalService from "src/services/agenda/sucursal";

const sessionStorageEffect =
  (key) =>
  ({ setSelf, onSet }) => {
    const savedValue = sessionStorage.getItem(key);
    if (savedValue !== null) {
      setSelf(JSON.parse(savedValue));
    }

    onSet((newValue, _, isReset) => {
      isReset
        ? sessionStorage.removeItem(key)
        : sessionStorage.setItem(key, JSON.stringify(newValue));
    });
  };

export const existPatientState = atom({
  key: "existPatientState",
  default: false,
  effects: [
    ({ onSet }) => {
      onSet((newValue) => {
        sessionStorage.setItem("existPatientState", JSON.stringify(newValue));
      });
    },
  ],
});

export const filterSearchSelectedState = atom({
  key: "filterSearchSelected",
  default: {
    specialitySelected: {
      value: "",
      code: null,
      showInFilter: true,
      canDeleteFilter: false,
    },
    professionalSelected: {
      value: "",
      code: null,
      showInFilter: true,
      canDeleteFilter: true,
    },
    covenantSelected: {
      value: "",
      code: null,
      showInFilter: true,
      canDeleteFilter: true,
    },
    tipoExamenSelected: {
      value: "",
      code: null,
      showInFilter: true,
      canDeleteFilter: false,
    },
    examenSelected: {
      value: "",
      code: null,
      showInFilter: true,
      canDeleteFilter: true,
    },
    sucursalSelected: {
      value: "",
      code: null,
      cmn_nombre: null,
      sue_correl: null,
      showInFilter: true,
      canDeleteFilter: false,
    },
    searchExternoSelected: {
      value: "",
    },
    datesRangeReviewDaysAvailable: {
      start: new Date().toISOString().split("T")[0],
      end: addDays(183).toISOString().split("T")[0],
    },
    servicioSelected: {
      value: "",
      code: null,
      showInFilter: true,
      canDeleteFilter: false,
    },
    procedimientoSelected: {
      value: "",
      code: null,
      showInFilter: true,
      canDeleteFilter: true,
    },
    modalidadSelected: {
      nombre: "",
      tipo: null,
      showInFilter: true,
      canDeleteFilter: false,
    },
  },
  effects_UNSTABLE: [sessionStorageEffect("filterSelected")],
});

export const isChangeFormSearchState = atom({
  key: "isChangeFormSearch",
  default: false,
});

export const filtersMappedState = selector({
  key: "filtersMapped",
  get: ({ get }) => {
    const filterSelected = get(filterSearchSelectedState);
    const keys = Object.keys(filterSelected);
    const values = Object.values(filterSelected);

    return values
      .map((el, index) => ({
        ...el,
        nameProperty: keys[index],
      }))
      .filter((x) => x.showInFilter === true && x.value !== "" && x.value);
  },
});

export const dayAvailableDefaultFetchState = selector({
  key: "dayAvailableDefaultFetch",
  get: async ({ get }) => {
    //get(dayDefaultAvailableState)
    const { professionalSelected, specialitySelected, sucursalSelected } = get(
      filterSearchSelectedState,
    );

    const professional = professionalSelected;
    const { code } = specialitySelected;

    if (specialitySelected.value === "" && professionalSelected.value === "")
      return null;

    const { data } = await ReservationService.getDayAvailableDefault({
      cod: code || "0",
      rut: professional.code || "0",
      ser_codigo: sucursalSelected.code,
    });

    if (data.data.length > 0) {
      const year = Number(data.data[0]["ageInicio"].substr(0, 4));
      const month = Number(data.data[0]["ageInicio"].substr(4, 2));
      const day = Number(data.data[0]["ageInicio"].substr(6, 2));

      return new Date(`${year}/${month}/${day}`);
    }

    return null;
  },
});

//------IMAGENOLOGIA------
export const dayAvailableDefaultImagenologiaFetchState = selector({
  key: "dayAvailableDefaultImagenologiaFetch",
  get: async ({ get }) => {
    //get(dayDefaultAvailableState)
    const { examenSelected, tipoExamenSelected, sucursalSelected } = get(
      filterSearchSelectedState,
    );

    const examen = examenSelected;
    const { code } = tipoExamenSelected;
    let tipoExamen = tipoExamenSelected.code;
    if (tipoExamenSelected.value === "" && examenSelected.value === "")
      return null;

    // const { data } = await ReservationService.getDayAvailableDefault({
    //     cod: code || '0',
    //     rut: tipoExamenSelected.code || '0'
    // });
    const { data } =
      await ReservationService.getDayAvailableImagenologiaDefault({
        cod: "170",
        exa_correl: examen.code,
        rut: tipoExamen,
        sue_correl: sucursalSelected.sue_correl,
      });

    if (data.data.length > 0) {
      const year = Number(data.data[0]["ageInicio"].substr(0, 4));
      const month = Number(data.data[0]["ageInicio"].substr(4, 2));
      const day = Number(data.data[0]["ageInicio"].substr(6, 2));

      return new Date(`${year}/${month}/${day}`);
    }

    return null;
  },
});

// ---------------- IMAGENOLOGIA ----------------
export const daysAvailableImagenologiaFetchState = selector({
  key: "daysAvailableImagenologiaFetch",
  get: async ({ get }) => {
    //get(daysAvailableState)
    const {
      examenSelected,
      tipoExamenSelected,
      datesRangeReviewDaysAvailable,
      sucursalSelected,
    } = get(filterSearchSelectedState);

    const examen = examenSelected;
    const { code } = "170"; // código de imagenologia //tipoExamenSelected;
    const { start, end } = datesRangeReviewDaysAvailable;
    if (tipoExamenSelected.value === "" && examenSelected.value === "")
      return [];

    // let { data } = await ReservationService.getDaysAvailable({
    //     rut: tipoExamenSelected.code || '0',
    //     cod: code,
    //     desde: start,
    //     hasta: end
    // });
    let { data } = await ReservationService.getDaysAvailableImagenologia({
      rut: tipoExamenSelected.code || "0",
      cod: code,
      desde: start,
      hasta: end,
      examen: examen.code,
      sue_correl: sucursalSelected.sue_correl,
    });

    return data.data.map((el) => {
      const year = Number(el.ageInicio.substr(0, 4));
      const month = Number(el.ageInicio.substr(4, 2));
      const day = Number(el.ageInicio.substr(6, 2));

      el["ageInicio"] = `${year}/${month}/${day}`;

      return el;
    });
  },
});

export const daysAvailableFetchState = selector({
  key: "daysAvailableFetch",
  get: async ({ get }) => {
    //get(daysAvailableState)
    const {
      professionalSelected,
      specialitySelected,
      datesRangeReviewDaysAvailable,
      sucursalSelected,
      modalidadSelected,
    } = get(filterSearchSelectedState);

    const professional = professionalSelected;
    const { code } = specialitySelected;
    const { start, end } = datesRangeReviewDaysAvailable;

    if (specialitySelected.value === "" && professionalSelected.value === "")
      return [];

    let { data } = await ReservationService.getDaysAvailable({
      rut: professional.code || "0",
      cod: code,
      desde: start,
      hasta: end,
      ser_codigo: sucursalSelected.code,
      tipo: modalidadSelected.tipo,
    });

    return data.data.map((el) => {
      const year = Number(el.ageInicio.substr(0, 4));
      const month = Number(el.ageInicio.substr(4, 2));
      const day = Number(el.ageInicio.substr(6, 2));

      el["ageInicio"] = `${year}/${month}/${day}`;

      return el;
    });
  },
});

export const daysAvailableState = atom({
  key: "daysAvailable",
  default: [],
});

export const hoursAvailableFetchState = selector({
  key: "hoursAvailableFetch",
  get: async ({ get }) => {
    //get(hoursAvailableState)
    const {
      professionalSelected,
      specialitySelected,
      covenantSelected,
      sucursalSelected,
      modalidadSelected,
    } = get(filterSearchSelectedState);

    const dayDefaultAvailable = get(dayDefaultAvailableState);
    if (!dayDefaultAvailable) return [];

    const { data } = await ReservationService.searchHoursAvailable(
      specialitySelected.code || "0",
      {
        convenio: covenantSelected.value?.toLowerCase(),
        profesionalRut: professionalSelected.code || "",
        date: dayDefaultAvailable?.toISOString().split("T")[0],
        SER_CODIGO: sucursalSelected.code,
      },
    );

    const dataFiltered = data.filter((el) => {
      if (modalidadSelected.tipo === "TEL") {
        return el.tipoAgenda === "TEL";
      } else if (modalidadSelected.tipo === "PRE") {
        return el.tipoAgenda === "PRE" || el.tipoAgenda === null;
      }
      return true;
    });

    return dataFiltered;
  },
});

export const hoursAvailableImagenologiaFetchState = selector({
  key: "hoursAvailableImagenologiaFetch",
  get: async ({ get }) => {
    //get(hoursAvailableState)
    const { tipoExamenSelected, examenSelected, sucursalSelected } = get(
      filterSearchSelectedState,
    );

    const dayDefaultAvailable = get(dayDefaultAvailableState);

    if (!dayDefaultAvailable) return [];

    // const { data } = await ReservationService.searchHoursAvailableImagenologia('170', {
    //     convenio: covenantSelected.value?.toLowerCase(),
    //     profesionalRut: professionalSelected.code || '',
    //     date: dayDefaultAvailable?.toISOString().split("T")[0]
    // });
    let codExam = examenSelected.code;
    let { data } = await ReservationService.searchHoursAvailableImagenologia(
      "170",
      codExam,
      {
        profesionalRut: tipoExamenSelected.code || "",
        exacorrel: examenSelected.code,
        date: dayDefaultAvailable?.toISOString().split("T")[0],
      },
    );

    if (process.env.REACT_APP_SEDE === "Calama") return data;
    console.log(sucursalSelected.sue_correl, "externo");

    if (sucursalSelected.code) {
      data = data.filter((el) => {
        return el.sucursalEmpresa === sucursalSelected.sue_correl;
      });
    }

    return data;
  },
});

export const reservationCanceledFecthState = selectorFamily({
  key: "reservationCanceledFecth",
  get: (uuid) => async () => {
    const { data } = await ReservationService.getReservationCanceled(uuid);

    return data;
  },
});

export const reservationCanceledState = atom({
  key: "reservationCanceled",
  default: [],
});

export const loadingDaysCalendarState = atom({
  key: "loadingDaysCalendar",
  default: false,
});

export const loadingHoursState = atom({
  key: "loadingHours",
  default: false,
});

export const professionalsMatchCovenantState = atom({
  key: "professionalsMatchCovenant",
  default: [],
});

export const professionalsNoMatchCovenantState = atom({
  key: "professionalsNoMatchCovenant",
  default: [],
});

export const hoursAvailableState = atom({
  key: "hoursAvailable",
  default: [],
});

export const activeTabState = atom({
  key: "activeTab",
  default: "1",
});

export const professionalsSinBloqueState = atom({
  key: "professionalsSinBloque",
  default: [],
});

// ---------------- PROCEDIMIENTO ----------------
export const dayAvailableDefaultProcedimientoFetchState = selector({
  key: "dayAvailableDefaultProcedimientoFetch",
  get: async ({ get }) => {
    const { servicioSelected, procedimientoSelected, sucursalSelected } = get(
      filterSearchSelectedState,
    );

    const servicio = servicioSelected;
    const procedimiento = procedimientoSelected;

    if (procedimientoSelected.value === "" && servicioSelected.value === "")
      return null;
    //?cod=227&rut=760189928&agp=16&ser_codigo=PCD&ase_correl=1
    const { data } =
      await ReservationService.getDayAvailableProcedimientoDefault({
        cod: "227",
        exa_correl: procedimiento.code,
        sue_correl: sucursalSelected.sue_correl,
      });

    if (data.data.length > 0) {
      const year = Number(data.data[0]["ageInicio"].substr(0, 4));
      const month = Number(data.data[0]["ageInicio"].substr(4, 2));
      const day = Number(data.data[0]["ageInicio"].substr(6, 2));

      return new Date(`${year}/${month}/${day}`);
    }

    return null;
  },
});

export const daysAvailableProcedimientoFetchState = selector({
  key: "daysAvailableProcedimientoFetch",
  get: async ({ get }) => {
    const {
      datesRangeReviewDaysAvailable,
      servicioSelected,
      procedimientoSelected,
      sucursalSelected,
    } = get(filterSearchSelectedState);

    const { start, end } = datesRangeReviewDaysAvailable;

    if (servicioSelected.value === "" && procedimientoSelected.value === "")
      return [];

    let { data } = await ReservationService.getDaysAvailableProcedimiento({
      desde: start,
      hasta: end,
      exa_correl: procedimientoSelected.code,
      sue_correl: sucursalSelected.sue_correl,
    });

    return data.data.map((el) => {
      const year = Number(el.ageInicio.substr(0, 4));
      const month = Number(el.ageInicio.substr(4, 2));
      const day = Number(el.ageInicio.substr(6, 2));

      el["ageInicio"] = `${year}/${month}/${day}`;

      return el;
    });
  },
});

export const hoursAvailableProcedimientoFetchState = selector({
  key: "hoursAvailableProcedimientoFetch",
  get: async ({ get }) => {
    const {
      servicioSelected,
      procedimientoSelected,
      covenantSelected,
      sucursalSelected,
    } = get(filterSearchSelectedState);

    const dayDefaultAvailable = get(dayDefaultAvailableState);
    if (!dayDefaultAvailable) return [];

    let codServ = servicioSelected.code;
    let exaCorrel = procedimientoSelected.code;
    const { data } = await ReservationService.searchHoursAvailableProcedimiento(
      codServ,
      exaCorrel,
      {
        exa_correl: procedimientoSelected.code,
        date: dayDefaultAvailable?.toISOString().split("T")[0],
        convenio: covenantSelected.value?.toLowerCase(),
        sue_correl: sucursalSelected.sue_correl,
      },
    );
    return data;
  },
});
